import React from 'react'
import { Span } from '@pandiafi/design-system'
import { CopyField } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import { TPaymentCategory } from '../../../../common/validations/paymentCategory'
import { Divider } from '../Divider'

import { useResolvePaymentCategory } from 'views/TerminateContract/hooks/usePaymentCategory'

interface BankAccountsPerCategoryProps {
  paymentCategories: TPaymentCategory[]
}

const compactIban = (iban: string) => iban.replace(/\s/g, '')

// Groups of four, as IBANs are printed on bills
const groupIban = (iban: string) =>
  compactIban(iban).replace(/(.{4})(?=.)/g, '$1 ')

export const BankAccountsPerCategory: React.FC<
  BankAccountsPerCategoryProps
> = ({ paymentCategories }) => {
  const { t } = useTranslation(['payments'])
  const { resolvePaymentCategory } = useResolvePaymentCategory()

  if (paymentCategories.length === 0) {
    return (
      <>
        <Divider className="mt-8" />
        <Span>{t('payments:noAccountData')}.</Span>
      </>
    )
  }

  return paymentCategories.map((pc) => {
    const category = resolvePaymentCategory(pc.category)

    return (
      <React.Fragment key={pc.category}>
        <Divider className="mt-8" />

        <div className="flex flex-col gap-5 mt-8">
          {pc.bankAccounts.map((ba) => (
            <CopyField
              key={ba.iban}
              label={
                pc.bankAccounts.length > 1
                  ? t('payments:bankAccountLabel', {
                      category,
                      bankName: ba.bankName,
                    })
                  : category
              }
              value={compactIban(ba.iban)}
            >
              {groupIban(ba.iban)}
              {pc.bankAccounts.length === 1 && ba.bankName
                ? ` (${ba.bankName})`
                : null}
            </CopyField>
          ))}
        </div>
      </React.Fragment>
    )
  })
}
